import forOwn from 'lodash/forOwn'
import isFunction from 'lodash/isFunction'
import { musicOnOff } from 'helpers/web_to_client'
import { serialize } from 'helpers'

const YOUTUBE_IFRAME_API_ENDPOINT = 'https://www.youtube.com/iframe_api'
const API_LOAD_TIMEOUT = 5 * 1000 // 5 sec

export class YoutubeVideo {
    _global
    _videoPlayers
    _isSoundMuted
    _YT
    _isLoaded

    constructor(global) {
        this._global = global

        this._videoPlayers = {}
        this._isSoundMuted = false
        this._YT = null
        this._isLoaded = false
    }

    muteSound() {
        musicOnOff(true)
    }

    unmuteSound() {
        musicOnOff(false)
    }

    isLoaded() {
        return this._isLoaded
    }

    loadAPI() {
        if (this._isLoaded) {
            return Promise.resolve()
        }

        const loadingPromise = new Promise((resolve) => {
            let apiLoadingTimer

            apiLoadingTimer = setTimeout(() => {
                this._isLoaded = true
                resolve()
            }, API_LOAD_TIMEOUT)

            this._global.onYouTubeIframeAPIReady = () => {
                this._YT = this._global.YT
                this._isLoaded = true
                clearTimeout(apiLoadingTimer)

                this._global.addEventListener('beforeunload', () => {
                    if (this._isSoundMuted) {
                        this.unmuteSound()
                    }
                })

                resolve()
            }
        })

        const youtubeApiScript = this._global.document.createElement('script')
        const firstScriptTag = this._global.document.getElementsByTagName('script')[0]
        youtubeApiScript.src = YOUTUBE_IFRAME_API_ENDPOINT
        firstScriptTag.parentNode.insertBefore(youtubeApiScript, firstScriptTag)

        return loadingPromise
    }

    /**
     * @param {string} id
     * @param {boolean} isPlayingForced
     */
    createPlayer(id, isPlayingForced) {
        let player = this._videoPlayers[id]
        const YT = this._YT

        if (player || !YT) {
            return
        }

        player = new YT.Player(id, {
            playerVars: {
                autoplay: (isPlayingForced ? 1 : 0),
            },
            events: {
                onReady: () => {
                    if (isPlayingForced) {
                        player.playVideo()
                    }
                },
                onStateChange: (data) => {
                    let isAnythingPlaying

                    switch (data.data) {
                    case YT.PlayerState.PLAYING:
                        forOwn(this._videoPlayers, (videoPlayer, videoPlayerId) => {
                            if (isFunction(videoPlayer.pauseVideo) && videoPlayerId !== id) {
                                videoPlayer.pauseVideo()
                            }
                        })

                        if (!this._isSoundMuted) {
                            this.muteSound()
                            this._isSoundMuted = true
                        }
                        break
                    case YT.PlayerState.ENDED:
                    case YT.PlayerState.PAUSED:
                        isAnythingPlaying = false
                        forOwn(this._videoPlayers, (videoPlayer) => {
                            if (videoPlayer.getPlayerState() === YT.PlayerState.PLAYING) {
                                isAnythingPlaying = true
                            }
                        })
                        if (!isAnythingPlaying && this._isSoundMuted) {
                            this.unmuteSound()
                            this._isSoundMuted = false
                        }
                        break
                    }
                },
            },
        })
        this._videoPlayers[id] = player
    }

    /**
     * @param {string} id
     */
    destroyPlayer(id) {
        const player = this._videoPlayers[id]

        if (!player || !this._YT) {
            return
        }

        if (this._isSoundMuted) {
            this.unmuteSound()
            this._isSoundMuted = false
        }

        this._videoPlayers[id] = null
    }
}

export const video = new YoutubeVideo(window)

/**
 * @param {string} youTubeId
 * @param {string} langCode
 * @param {boolean} show_subtitles
 * @param {boolean} autoplay
 * @returns {string}
 */
export const generateYouTubeLink = (youTubeId, langCode, show_subtitles = false, autoplay = false) => {
    const params = {
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        disablekb: 1,
        hl: langCode,
        controls: 1,
        enablejsapi: 1,
        autoplay: autoplay ? 1 : 0,
        cc_load_policy: 0,
    }
    if (show_subtitles) {
        params.cc_lang_pref = langCode
        params.cc_load_policy = 1
    }
    return `${youTubeId}${serialize(params)}`
}



// WEBPACK FOOTER //
// ./components/Youtube/YoutubeVideo.js